/**
 * Bake computed presentation styles into a rendered Mermaid SVG so the
 * diagram survives environments that strip every stylesheet — most notably
 * Safari Reader Mode, which keeps the element tree and inline style
 * attributes but drops all CSS rules (the page sheet and the SVG-internal
 * theme sheet alike). Without baking, SVG defaults take over (fill/stroke
 * black) and the diagram collapses into unreadable black blocks.
 *
 * Carriers:
 *  - SVG elements: presentation ATTRIBUTES (`fill="…"`, `stroke="…"`).
 *    Attributes lose to every stylesheet rule, so normal rendering with
 *    CSS present stays pixel-identical; they only surface once CSS is gone.
 *  - foreignObject HTML: inline style declarations (HTML has no
 *    presentation attributes); Reader preserves inline styles.
 *
 * Minimal-diff: a property is baked only when its computed value differs
 * from what the element resolves to without any CSS (spec initial value or
 * the nearest inherited ancestor value), so markup growth stays
 * proportional to the diagram's actual theming.
 */

export type ComputedStyleResolver = (element: Element) => CSSStyleDeclaration;

interface BakeProp {
	css: string;
	/** Computed-value form of the spec initial value, as returned by getComputedStyle in the rendering engine. */
	initial: string;
	inherited: boolean;
	/** Presentation attribute name; omit for properties without one (inline style is used instead). */
	attr?: string;
	domain: 'svg' | 'html';
}

const SVG_PROPS: BakeProp[] = [
	{ css: 'fill', initial: 'rgb(0, 0, 0)', inherited: true, attr: 'fill', domain: 'svg' },
	{ css: 'fill-opacity', initial: '1', inherited: true, attr: 'fill-opacity', domain: 'svg' },
	{ css: 'fill-rule', initial: 'nonzero', inherited: true, attr: 'fill-rule', domain: 'svg' },
	{ css: 'stroke', initial: 'none', inherited: true, attr: 'stroke', domain: 'svg' },
	{ css: 'stroke-width', initial: '1px', inherited: true, attr: 'stroke-width', domain: 'svg' },
	{ css: 'stroke-opacity', initial: '1', inherited: true, attr: 'stroke-opacity', domain: 'svg' },
	{ css: 'stroke-linecap', initial: 'butt', inherited: true, attr: 'stroke-linecap', domain: 'svg' },
	{ css: 'stroke-linejoin', initial: 'miter', inherited: true, attr: 'stroke-linejoin', domain: 'svg' },
	{ css: 'stroke-miterlimit', initial: '4', inherited: true, attr: 'stroke-miterlimit', domain: 'svg' },
	{ css: 'stroke-dasharray', initial: 'none', inherited: true, attr: 'stroke-dasharray', domain: 'svg' },
	{ css: 'stroke-dashoffset', initial: '0px', inherited: true, attr: 'stroke-dashoffset', domain: 'svg' },
	{ css: 'opacity', initial: '1', inherited: false, attr: 'opacity', domain: 'svg' },
	{ css: 'text-anchor', initial: 'start', inherited: true, attr: 'text-anchor', domain: 'svg' },
	{ css: 'dominant-baseline', initial: 'auto', inherited: false, attr: 'dominant-baseline', domain: 'svg' },
	{ css: 'paint-order', initial: 'normal', inherited: false, attr: 'paint-order', domain: 'svg' },
	{ css: 'stop-color', initial: 'rgb(0, 0, 0)', inherited: true, attr: 'stop-color', domain: 'svg' },
	{ css: 'stop-opacity', initial: '1', inherited: true, attr: 'stop-opacity', domain: 'svg' },
	{ css: 'clip-path', initial: 'none', inherited: false, attr: 'clip-path', domain: 'svg' },
	{ css: 'marker-start', initial: 'none', inherited: false, attr: 'marker-start', domain: 'svg' },
	{ css: 'marker-mid', initial: 'none', inherited: false, attr: 'marker-mid', domain: 'svg' },
	{ css: 'marker-end', initial: 'none', inherited: false, attr: 'marker-end', domain: 'svg' },
	{ css: 'color', initial: 'rgb(0, 0, 0)', inherited: true, attr: 'color', domain: 'svg' },
	{ css: 'font-size', initial: '16px', inherited: true, attr: 'font-size', domain: 'svg' },
	{ css: 'font-weight', initial: '400', inherited: true, attr: 'font-weight', domain: 'svg' },
	{ css: 'font-style', initial: 'normal', inherited: true, attr: 'font-style', domain: 'svg' },
	// No reliable initial literal for font-family across engines; an empty
	// expectation bakes the first computed value (the svg root) and
	// descendants then compare against inheritance.
	{ css: 'font-family', initial: '', inherited: true, attr: 'font-family', domain: 'svg' },
];

const HTML_PROPS: BakeProp[] = [
	{ css: 'color', initial: 'rgb(0, 0, 0)', inherited: true, domain: 'html' },
	{ css: 'background-color', initial: 'rgba(0, 0, 0, 0)', inherited: false, domain: 'html' },
	{ css: 'font-size', initial: '16px', inherited: true, domain: 'html' },
	{ css: 'font-weight', initial: '400', inherited: true, domain: 'html' },
	{ css: 'font-style', initial: 'normal', inherited: true, domain: 'html' },
	{ css: 'font-family', initial: '', inherited: true, domain: 'html' },
	{ css: 'line-height', initial: 'normal', inherited: true, domain: 'html' },
	{ css: 'text-align', initial: 'start', inherited: true, domain: 'html' },
	{ css: 'white-space', initial: 'normal', inherited: true, domain: 'html' },
	{ css: 'letter-spacing', initial: 'normal', inherited: true, domain: 'html' },
	{ css: 'text-decoration', initial: 'none', inherited: false, domain: 'html' },
];

export function bakeMermaidSvgSelfContainedStyles(
	svg: SVGSVGElement,
	resolveStyle: ComputedStyleResolver = (el) => window.getComputedStyle(el),
): void {
	bakeElement(svg, new Map(), resolveStyle);
}

const DARK_INK = 'rgb(29, 29, 31)';
const LIGHT_INK = 'rgb(245, 247, 250)';
/** WCAG AA bar for body-size text. */
const MIN_LABEL_CONTRAST = 4.5;

type Rgba = [number, number, number, number];

function parseRgb(value: string): Rgba | null {
	const hex = value.trim().match(/^#([0-9a-f]{6})$/i);
	if (hex) {
		const int = parseInt(hex[1], 16);
		return [(int >> 16) & 255, (int >> 8) & 255, int & 255, 1];
	}
	const match = value.match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)/);
	if (!match) return null;
	return [Number(match[1]), Number(match[2]), Number(match[3]), match[4] === undefined ? 1 : Number(match[4])];
}

function srgbChannelLuminance(component: number): number {
	const scaled = component / 255;
	return scaled <= 0.03928 ? scaled / 12.92 : Math.pow((scaled + 0.055) / 1.055, 2.4);
}

function relativeLuminance(color: Rgba): number {
	return 0.2126 * srgbChannelLuminance(color[0]) + 0.7152 * srgbChannelLuminance(color[1]) + 0.0722 * srgbChannelLuminance(color[2]);
}

function contrastRatio(a: Rgba, b: Rgba): number {
	const la = relativeLuminance(a);
	const lb = relativeLuminance(b);
	return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Repair node-label readability where the node background and the theme
 * label color collide — authored `style X fill:#e1d5e7` statements carry no
 * text color, so against the dark theme's near-white labels they render as
 * white text on pastel fills. When a node's effective background falls below
 * the contrast bar against any of its label colors, every label element gets
 * the better-contrasting ink inlined. Inline declarations beat all
 * stylesheet rules, so the correction holds in normal rendering and
 * survives stylesheet-stripping environments such as Safari Reader Mode.
 * Transparent, gradient, and already-readable fills are left untouched.
 */
export function reconcileMermaidLabelContrast(
	svg: SVGSVGElement,
	resolveStyle: ComputedStyleResolver = (el) => window.getComputedStyle(el),
): void {
	for (const node of Array.from(svg.querySelectorAll('g.node'))) reconcileNodeLabelContrast(node, resolveStyle);
}

/**
 * A node's painted background is an SVG geometry element carrying an opaque
 * fill. Mermaid diagram types disagree on structure: flowchart nodes expose
 * rect.basic.label-container directly, while classDiagram wraps the box path
 * inside g.basic.label-container — a group whose computed fill is the label
 * INK (inherited from text CSS), not a background. Matching that group
 * misreads the label ink as the node background and flips every label to the
 * opposite ink, so only geometry elements qualify here.
 */
function findNodeBackgroundShape(node: Element, resolveStyle: ComputedStyleResolver): Element | null {
	for (const candidate of Array.from(node.querySelectorAll('rect, path, polygon, circle, ellipse'))) {
		const fill = parseRgb(resolveStyle(candidate).getPropertyValue('fill'));
		if (fill && fill[3] >= 0.5) return candidate;
	}
	return null;
}

function reconcileNodeLabelContrast(node: Element, resolveStyle: ComputedStyleResolver): void {
	const shape = findNodeBackgroundShape(node, resolveStyle);
	if (!shape) return;
	const fill = parseRgb(resolveStyle(shape).getPropertyValue('fill'));
	if (!fill || fill[3] < 0.5) return;

	const foreignObject = node.querySelector('foreignObject');
	let targets: Element[];
	let colorProp: 'color' | 'fill';
	if (foreignObject) {
		colorProp = 'color';
		// Inline every descendant, not just the spans: a later stylesheet rule
		// on an inner element (e.g. article `p` styles) would otherwise beat
		// the inherited inline color again.
		targets = [foreignObject, ...Array.from(foreignObject.querySelectorAll('*'))];
	} else {
		colorProp = 'fill';
		targets = Array.from(node.querySelectorAll('text, tspan'));
	}
	if (targets.length === 0) return;

	const darkInk = parseRgb(DARK_INK) as Rgba;
	const lightInk = parseRgb(LIGHT_INK) as Rgba;
	const ink = contrastRatio(fill, darkInk) >= contrastRatio(fill, lightInk) ? DARK_INK : LIGHT_INK;
	// Judge readability on the elements that actually carry a resolved color;
	// containers without one (or a transparent one) must not force a rewrite.
	const colors = targets.map((target) => parseRgb(resolveStyle(target).getPropertyValue(colorProp)));
	const readable = colors.some((color) => color !== null) && colors.every((color) => color === null || contrastRatio(fill, color) >= MIN_LABEL_CONTRAST);
	if (readable) return;

	for (const target of targets) (target as HTMLElement | SVGElement).style.setProperty(colorProp, ink);
}

function bakeElement(el: Element, expectedInherited: Map<string, string>, resolveStyle: ComputedStyleResolver): void {
	const tag = el.tagName.toLowerCase();
	if (tag === 'style' || tag === 'title' || tag === 'desc') return;
	const htmlEl = el instanceof HTMLElement ? el : null;
	const computed = resolveStyle(el);
	const forChildren = new Map(expectedInherited);
	for (const prop of (htmlEl ? HTML_PROPS : SVG_PROPS)) {
		const value = computed.getPropertyValue(prop.css);
		if (!value) continue;
		const expected = expectedInherited.get(prop.css) ?? prop.initial;
		if (value === expected) continue;
		if (htmlEl) htmlEl.style.setProperty(prop.css, value);
		else el.setAttribute(prop.attr as string, value);
		if (prop.inherited) forChildren.set(prop.css, value);
	}
	for (const child of Array.from(el.children)) bakeElement(child, forChildren, resolveStyle);
}
